import { type DateRange } from "@src/appV2/lib";
import { endOfMonth, startOfMonth } from "date-fns";
import { useCallback } from "react";

import { getCalendarPickerDateRange } from "./getCalendarPickerDateRange";

interface UseCalendarLoadMoreProps {
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange) => void;
  monthByMonthLoading: boolean;
  fetchMoreWorkerShifts: (params: { pageParam: DateRange }) => Promise<any>;
  fetchMoreOpenShiftsCount: (params: { pageParam: DateRange }) => Promise<any>;
  urgentShiftsPagination?: {
    fetchNextPage: (params: { pageParam: DateRange }) => Promise<any>;
  };
}

export function useCalendarLoadMore(props: UseCalendarLoadMoreProps) {
  const {
    dateRange,
    setDateRange,
    monthByMonthLoading,
    fetchMoreWorkerShifts,
    fetchMoreOpenShiftsCount,
    urgentShiftsPagination,
  } = props;

  const getNextDateRange = useCallback(
    (targetMonth?: Date): DateRange => {
      if (monthByMonthLoading && targetMonth) {
        return {
          startDate: startOfMonth(targetMonth),
          endDate: endOfMonth(targetMonth),
        };
      }

      return getCalendarPickerDateRange(dateRange.endDate);
    },
    [monthByMonthLoading, dateRange.endDate]
  );

  const getUpdatedDateRange = useCallback(
    (nextDateRangeToFetch: DateRange): DateRange => {
      if (monthByMonthLoading) {
        return {
          startDate:
            // FIXME: Automatically disabled during ESLint config update
            // eslint-disable-next-line unicorn/prefer-math-min-max
            dateRange.startDate < nextDateRangeToFetch.startDate
              ? dateRange.startDate
              : nextDateRangeToFetch.startDate,
          endDate:
            // FIXME: Automatically disabled during ESLint config update
            // eslint-disable-next-line unicorn/prefer-math-min-max
            dateRange.endDate > nextDateRangeToFetch.endDate
              ? dateRange.endDate
              : nextDateRangeToFetch.endDate,
        };
      }

      return {
        startDate: dateRange.startDate,
        endDate: nextDateRangeToFetch.endDate,
      };
    },
    [monthByMonthLoading, dateRange]
  );

  const loadMore = useCallback(
    async (targetMonth?: Date) => {
      const nextDateRangeToFetch = getNextDateRange(targetMonth);

      await Promise.all([
        fetchMoreWorkerShifts({ pageParam: nextDateRangeToFetch }),
        fetchMoreOpenShiftsCount({ pageParam: nextDateRangeToFetch }),
        urgentShiftsPagination?.fetchNextPage({ pageParam: nextDateRangeToFetch }),
      ]);

      const newDateRange = getUpdatedDateRange(nextDateRangeToFetch);
      setDateRange(newDateRange);
    },
    [
      fetchMoreWorkerShifts,
      fetchMoreOpenShiftsCount,
      urgentShiftsPagination,
      setDateRange,
      getNextDateRange,
      getUpdatedDateRange,
    ]
  );

  return { loadMore };
}
